import { __awaiter } from 'tslib';
import { OpenFeatureError, GeneralError, ProviderStatus, ErrorCode, ProviderEvents, OpenFeatureEventEmitter, DefaultLogger, StandardResolutionReasons } from '@openfeature/web-sdk';

/**
 * Utility for executing a set of hooks of each type. Implementation is largely copied from the main OpenFeature SDK.
 */
class HookExecutor {
    constructor(logger) {
        this.logger = logger;
    }
    beforeHooks(hooks, hookContext, hints) {
        var _a;
        for (const hook of hooks !== null && hooks !== void 0 ? hooks : []) {
            (_a = hook === null || hook === void 0 ? void 0 : hook.before) === null || _a === void 0 ? void 0 : _a.call(hook, hookContext, Object.freeze(hints));
        }
    }
    afterHooks(hooks, hookContext, evaluationDetails, hints) {
        var _a;
        // run "after" hooks sequentially
        for (const hook of hooks !== null && hooks !== void 0 ? hooks : []) {
            (_a = hook === null || hook === void 0 ? void 0 : hook.after) === null || _a === void 0 ? void 0 : _a.call(hook, hookContext, evaluationDetails, hints);
        }
    }
    errorHooks(hooks, hookContext, err, hints) {
        var _a;
        // run "error" hooks sequentially
        for (const hook of hooks !== null && hooks !== void 0 ? hooks : []) {
            try {
                (_a = hook === null || hook === void 0 ? void 0 : hook.error) === null || _a === void 0 ? void 0 : _a.call(hook, hookContext, err, hints);
            }
            catch (err) {
                this.logger.error(`Unhandled error during 'error' hook: ${err}`);
                if (err instanceof Error) {
                    this.logger.error(err.stack);
                }
                this.logger.error(err === null || err === void 0 ? void 0 : err.stack);
            }
        }
    }
    finallyHooks(hooks, hookContext, evaluationDetails, hints) {
        var _a;
        // run "finally" hooks sequentially
        for (const hook of hooks !== null && hooks !== void 0 ? hooks : []) {
            try {
                (_a = hook === null || hook === void 0 ? void 0 : hook.finally) === null || _a === void 0 ? void 0 : _a.call(hook, hookContext, evaluationDetails, hints);
            }
            catch (err) {
                this.logger.error(`Unhandled error during 'finally' hook: ${err}`);
                if (err instanceof Error) {
                    this.logger.error(err.stack);
                }
                this.logger.error(err === null || err === void 0 ? void 0 : err.stack);
            }
        }
    }
}

class ErrorWithCode extends OpenFeatureError {
    constructor(code, message) {
        super(message);
        this.code = code;
    }
}
class AggregateError extends GeneralError {
    constructor(message, originalErrors) {
        super(message);
        this.originalErrors = originalErrors;
    }
}
const constructAggregateError = (providerErrors) => {
    const errorsWithSource = providerErrors
        .map(({ providerName, error }) => {
        return { source: providerName, error };
    })
        .flat();
    // log first error in the message for convenience, but include all errors in the error object for completeness
    return new AggregateError(`Provider errors occurred: ${errorsWithSource[0].source}: ${errorsWithSource[0].error}`, errorsWithSource);
};
const throwAggregateErrorFromPromiseResults = (result, providerEntries) => {
    const errors = result
        .map((r, i) => {
        if (r.status === 'rejected') {
            return { error: r.reason, providerName: providerEntries[i].name };
        }
        return null;
    })
        .filter((val) => Boolean(val));
    if (errors.length) {
        throw constructAggregateError(errors);
    }
};

/**
 * Base strategy to inherit from. Not directly usable, as strategies must implement the "determineResult" method
 * Contains default implementations for `shouldEvaluateThisProvider` and `shouldEvaluateNextProvider`
 */
class BaseEvaluationStrategy {
    shouldEvaluateThisProvider(strategyContext, evalContext) {
        if (strategyContext.providerStatus === ProviderStatus.NOT_READY ||
            strategyContext.providerStatus === ProviderStatus.FATAL) {
            return false;
        }
        return true;
    }
    shouldEvaluateNextProvider(strategyContext, context, result) {
        return true;
    }
    hasError(resolution) {
        return 'thrownError' in resolution || !!resolution.details.errorCode;
    }
    hasErrorWithCode(resolution, code) {
        var _a;
        return 'thrownError' in resolution
            ? ((_a = resolution.thrownError) === null || _a === void 0 ? void 0 : _a.code) === code
            : resolution.details.errorCode === code;
    }
    collectProviderErrors(resolutions) {
        var _a;
        const errors = [];
        for (const resolution of resolutions) {
            if ('thrownError' in resolution) {
                errors.push({ providerName: resolution.providerName, error: resolution.thrownError });
            }
            else if (resolution.details.errorCode) {
                errors.push({
                    providerName: resolution.providerName,
                    error: new ErrorWithCode(resolution.details.errorCode, (_a = resolution.details.errorMessage) !== null && _a !== void 0 ? _a : 'unknown error'),
                });
            }
        }
        return { errors };
    }
    resolutionToFinalResult(resolution) {
        return { details: resolution.details, provider: resolution.provider, providerName: resolution.providerName };
    }
}

/**
 * Return the first result that did not indicate "flag not found".
 * If any provider in the course of evaluation returns or throws an error, throw that error
 */
class FirstMatchStrategy extends BaseEvaluationStrategy {
    shouldEvaluateNextProvider(strategyContext, context, result) {
        if (this.hasErrorWithCode(result, ErrorCode.FLAG_NOT_FOUND)) {
            return true;
        }
        if (this.hasError(result)) {
            return false;
        }
        return false;
    }
    determineFinalResult(strategyContext, context, resolutions) {
        const finalResolution = resolutions[resolutions.length - 1];
        if (this.hasError(finalResolution)) {
            return this.collectProviderErrors(resolutions);
        }
        return this.resolutionToFinalResult(finalResolution);
    }
}

/**
 * Return the first result that did result in an error
 * If any provider in the course of evaluation returns or throws an error, ignore it as long as there is a successful result
 * If there is no successful result, throw all errors
 */
class FirstSuccessfulStrategy extends BaseEvaluationStrategy {
    shouldEvaluateNextProvider(strategyContext, context, result) {
        // evaluate next only if there was an error
        return this.hasError(result);
    }
    determineFinalResult(strategyContext, context, resolutions) {
        const finalResolution = resolutions[resolutions.length - 1];
        if (this.hasError(finalResolution)) {
            return this.collectProviderErrors(resolutions);
        }
        return this.resolutionToFinalResult(finalResolution);
    }
}

/**
 * Evaluate all providers and compare the results.
 * If the values agree, return the value
 * If the values disagree, return the value from the configured "fallback provider" and execute the "onMismatch"
 * callback if defined
 */
class ComparisonStrategy extends BaseEvaluationStrategy {
    constructor(fallbackProvider, onMismatch) {
        super();
        this.fallbackProvider = fallbackProvider;
        this.onMismatch = onMismatch;
    }
    determineFinalResult(strategyContext, context, resolutions) {
        var _a;
        let value;
        let fallbackResolution;
        let finalResolution;
        let mismatch = false;
        for (const [i, resolution] of resolutions.entries()) {
            if (this.hasError(resolution)) {
                return this.collectProviderErrors(resolutions);
            }
            if (resolution.provider === this.fallbackProvider) {
                fallbackResolution = resolution;
            }
            if (i === 0) {
                finalResolution = resolution;
            }
            if (typeof value !== 'undefined' && value !== resolution.details.value) {
                mismatch = true;
            }
            else {
                value = resolution.details.value;
            }
        }
        if (!fallbackResolution) {
            throw new GeneralError('Fallback provider not found in resolution results');
        }
        if (!finalResolution) {
            throw new GeneralError('Final resolution not found in resolution results');
        }
        if (mismatch) {
            (_a = this.onMismatch) === null || _a === void 0 ? void 0 : _a.call(this, resolutions);
            return {
                details: fallbackResolution.details,
                provider: fallbackResolution.provider,
            };
        }
        return this.resolutionToFinalResult(finalResolution);
    }
}

/**
 * Tracks each individual provider's status by listening to emitted events
 * Maintains an overall "status" for the multi provider which represents the "most critical" status out of all providers
 */
class StatusTracker {
    constructor(events) {
        this.events = events;
        this.providerStatuses = {};
    }
    wrapEventHandler(providerEntry) {
        var _a, _b, _c, _d;
        const provider = providerEntry.provider;
        (_a = provider.events) === null || _a === void 0 ? void 0 : _a.addHandler(ProviderEvents.Error, (details) => {
            this.changeProviderStatus(providerEntry.name, ProviderStatus.ERROR, details);
        });
        (_b = provider.events) === null || _b === void 0 ? void 0 : _b.addHandler(ProviderEvents.Stale, (details) => {
            this.changeProviderStatus(providerEntry.name, ProviderStatus.STALE, details);
        });
        (_c = provider.events) === null || _c === void 0 ? void 0 : _c.addHandler(ProviderEvents.ConfigurationChanged, (details) => {
            this.events.emit(ProviderEvents.ConfigurationChanged, details);
        });
        (_d = provider.events) === null || _d === void 0 ? void 0 : _d.addHandler(ProviderEvents.Ready, (details) => {
            this.changeProviderStatus(providerEntry.name, ProviderStatus.READY, details);
        });
    }
    providerStatus(name) {
        return this.providerStatuses[name];
    }
    getStatusFromProviderStatuses() {
        const statuses = Object.values(this.providerStatuses);
        if (statuses.includes(ProviderStatus.FATAL)) {
            return ProviderStatus.FATAL;
        }
        else if (statuses.includes(ProviderStatus.NOT_READY)) {
            return ProviderStatus.NOT_READY;
        }
        else if (statuses.includes(ProviderStatus.ERROR)) {
            return ProviderStatus.ERROR;
        }
        else if (statuses.includes(ProviderStatus.STALE)) {
            return ProviderStatus.STALE;
        }
        return ProviderStatus.READY;
    }
    changeProviderStatus(name, status, details) {
        const currentStatus = this.getStatusFromProviderStatuses();
        this.providerStatuses[name] = status;
        const newStatus = this.getStatusFromProviderStatuses();
        if (currentStatus !== newStatus) {
            if (newStatus === ProviderStatus.FATAL) {
                this.events.emit(ProviderEvents.Error, details);
            }
            else if (newStatus === ProviderStatus.ERROR) {
                this.events.emit(ProviderEvents.Error, details);
            }
            else if (newStatus === ProviderStatus.STALE) {
                this.events.emit(ProviderEvents.Stale, details);
            }
            else if (newStatus === ProviderStatus.READY) {
                this.events.emit(ProviderEvents.Ready, details);
            }
        }
    }
}

class WebMultiProvider {
    constructor(constructorProviders, evaluationStrategy = new FirstMatchStrategy(), logger = new DefaultLogger()) {
        this.constructorProviders = constructorProviders;
        this.evaluationStrategy = evaluationStrategy;
        this.logger = logger;
        this.runsOn = 'client';
        this.events = new OpenFeatureEventEmitter();
        this.hookContexts = new WeakMap();
        this.hookHints = new WeakMap();
        this.providerEntries = [];
        this.providerEntriesByName = {};
        this.statusTracker = new StatusTracker(this.events);
        this.hookExecutor = new HookExecutor(this.logger);
        this.registerProviders(constructorProviders);
        const aggregateMetadata = Object.keys(this.providerEntriesByName).reduce((acc, name) => {
            return Object.assign(Object.assign({}, acc), { [name]: this.providerEntriesByName[name].provider.metadata });
        }, {});
        this.metadata = Object.assign(Object.assign({}, aggregateMetadata), { name: WebMultiProvider.name });
    }
    registerProviders(constructorProviders) {
        var _a, _b;
        const providersByName = {};
        for (const constructorProvider of constructorProviders) {
            const providerName = constructorProvider.provider.metadata.name;
            const candidateName = (_a = constructorProvider.name) !== null && _a !== void 0 ? _a : providerName;
            if (constructorProvider.name && providersByName[constructorProvider.name]) {
                throw new Error('Provider names must be unique');
            }
            (_b = providersByName[candidateName]) !== null && _b !== void 0 ? _b : (providersByName[candidateName] = []);
            providersByName[candidateName].push(constructorProvider.provider);
        }
        for (const name of Object.keys(providersByName)) {
            const useIndexedNames = providersByName[name].length > 1;
            for (let i = 0; i < providersByName[name].length; i++) {
                const indexedName = useIndexedNames ? `${name}-${i + 1}` : name;
                this.providerEntriesByName[indexedName] = { provider: providersByName[name][i], name: indexedName };
                this.providerEntries.push(this.providerEntriesByName[indexedName]);
                this.statusTracker.wrapEventHandler(this.providerEntriesByName[indexedName]);
            }
        }
        // just make sure we don't accidentally modify these later
        Object.freeze(this.providerEntries);
        Object.freeze(this.providerEntriesByName);
    }
    initialize(context) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield Promise.allSettled(this.providerEntries.map((provider) => { var _a, _b; return (_b = (_a = provider.provider).initialize) === null || _b === void 0 ? void 0 : _b.call(_a, context); }));
            throwAggregateErrorFromPromiseResults(result, this.providerEntries);
        });
    }
    onClose() {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield Promise.allSettled(this.providerEntries.map((provider) => { var _a, _b; return (_b = (_a = provider.provider).onClose) === null || _b === void 0 ? void 0 : _b.call(_a); }));
            throwAggregateErrorFromPromiseResults(result, this.providerEntries);
        });
    }
    onContextChange(oldContext, newContext) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            for (const providerEntry of this.providerEntries) {
                yield ((_b = (_a = providerEntry.provider).onContextChange) === null || _b === void 0 ? void 0 : _b.call(_a, oldContext, newContext));
            }
        });
    }
    resolveBooleanEvaluation(flagKey, defaultValue, context) {
        return this.flagResolutionProxy(flagKey, 'boolean', defaultValue, context);
    }
    resolveStringEvaluation(flagKey, defaultValue, context) {
        return this.flagResolutionProxy(flagKey, 'string', defaultValue, context);
    }
    resolveNumberEvaluation(flagKey, defaultValue, context) {
        return this.flagResolutionProxy(flagKey, 'number', defaultValue, context);
    }
    resolveObjectEvaluation(flagKey, defaultValue, context) {
        return this.flagResolutionProxy(flagKey, 'object', defaultValue, context);
    }
    flagResolutionProxy(flagKey, flagType, defaultValue, context) {
        var _a;
        const hookContext = this.hookContexts.get(context);
        const hookHints = this.hookHints.get(context);
        if (!hookContext || !hookHints) {
            throw new GeneralError('Hook context not available for evaluation');
        }
        const results = [];
        for (const providerEntry of this.providerEntries) {
            const [shouldEvaluateNext, result] = this.evaluateProviderEntry(flagKey, flagType, defaultValue, providerEntry, hookContext, hookHints, context);
            results.push(result);
            if (!shouldEvaluateNext) {
                break;
            }
        }
        const resolutions = results.filter((r) => Boolean(r));
        const finalResult = this.evaluationStrategy.determineFinalResult({ flagKey, flagType }, context, resolutions);
        if ((_a = finalResult.errors) === null || _a === void 0 ? void 0 : _a.length) {
            throw constructAggregateError(finalResult.errors);
        }
        if (!finalResult.details) {
            throw new GeneralError('No result was returned from any provider');
        }
        return finalResult.details;
    }
    evaluateProviderEntry(flagKey, flagType, defaultValue, providerEntry, hookContext, hookHints, context) {
        let evaluationResult = undefined;
        const provider = providerEntry.provider;
        const strategyContext = {
            flagKey,
            flagType,
            provider,
            providerName: providerEntry.name,
            providerStatus: this.statusTracker.providerStatus(providerEntry.name),
        };
        if (!this.evaluationStrategy.shouldEvaluateThisProvider(strategyContext, context)) {
            return [true, null];
        }
        let resolution;
        try {
            evaluationResult = this.evaluateProviderAndHooks(flagKey, defaultValue, provider, hookContext, hookHints);
            resolution = {
                details: evaluationResult,
                provider: provider,
                providerName: providerEntry.name,
            };
        }
        catch (error) {
            resolution = {
                thrownError: error,
                provider: provider,
                providerName: providerEntry.name,
            };
        }
        return [this.evaluationStrategy.shouldEvaluateNextProvider(strategyContext, context, resolution), resolution];
    }
    evaluateProviderAndHooks(flagKey, defaultValue, provider, hookContext, hookHints) {
        var _a;
        let evaluationDetails;
        try {
            this.hookExecutor.beforeHooks(provider.hooks, hookContext, hookHints);
            const resolutionDetails = this.callProviderResolve(provider, flagKey, defaultValue, hookContext.context);
            evaluationDetails = Object.assign(Object.assign({}, resolutionDetails), { flagMetadata: Object.freeze((_a = resolutionDetails.flagMetadata) !== null && _a !== void 0 ? _a : {}), flagKey });
            this.hookExecutor.afterHooks(provider.hooks, hookContext, evaluationDetails, hookHints);
        }
        catch (error) {
            this.hookExecutor.errorHooks(provider.hooks, hookContext, error, hookHints);
            evaluationDetails = this.getErrorEvaluationDetails(flagKey, defaultValue, error);
        }
        this.hookExecutor.finallyHooks(provider.hooks, hookContext, evaluationDetails, hookHints);
        return evaluationDetails;
    }
    callProviderResolve(provider, flagKey, defaultValue, context) {
        switch (typeof defaultValue) {
            case 'string':
                return provider.resolveStringEvaluation(flagKey, defaultValue, context, this.logger);
            case 'number':
                return provider.resolveNumberEvaluation(flagKey, defaultValue, context, this.logger);
            case 'object':
                return provider.resolveObjectEvaluation(flagKey, defaultValue, context, this.logger);
            case 'boolean':
                return provider.resolveBooleanEvaluation(flagKey, defaultValue, context, this.logger);
            default:
                throw new GeneralError('Invalid flag evaluation type');
        }
    }
    get hooks() {
        return [
            {
                before: (hookContext, hints) => {
                    this.hookContexts.set(hookContext.context, hookContext);
                    this.hookHints.set(hookContext.context, hints !== null && hints !== void 0 ? hints : {});
                    return hookContext.context;
                },
            },
        ];
    }
    getErrorEvaluationDetails(flagKey, defaultValue, err, flagMetadata = {}) {
        const errorMessage = err === null || err === void 0 ? void 0 : err.message;
        const errorCode = (err === null || err === void 0 ? void 0 : err.code) || ErrorCode.GENERAL;
        return {
            errorCode,
            errorMessage,
            value: defaultValue,
            reason: StandardResolutionReasons.ERROR,
            flagMetadata: Object.freeze(flagMetadata),
            flagKey,
        };
    }
}

export { AggregateError, BaseEvaluationStrategy, ComparisonStrategy, ErrorWithCode, FirstMatchStrategy, FirstSuccessfulStrategy, WebMultiProvider, constructAggregateError, throwAggregateErrorFromPromiseResults };
